import { chakra, Checkbox, Code, useColorModeValue } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import type { FormFields } from '../types';
import type { SmartContractVerificationConfig } from 'types/client/contract';

import { getResourceKey } from 'lib/api/useApiQuery';
import { useI18n } from 'lib/i18n/context';
import FormFieldFancySelect from 'ui/shared/forms/fields/FormFieldFancySelect';
import IconSvg from 'ui/shared/IconSvg';

import ContractVerificationFormRow from '../ContractVerificationFormRow';
import { CONTRACT_VERIFICATION_FIELD_KEYS } from '../i18n';

const OPTIONS_LIMIT = 50;

interface Props {
  isVyper?: boolean;
  isStylus?: boolean;
}

const ContractVerificationFieldCompiler = ({ isVyper, isStylus }: Props) => {
  const { t } = useI18n();
  const [ isNightly, setIsNightly ] = React.useState(false);
  const { formState, getValues, resetField } = useFormContext<FormFields>();
  const queryClient = useQueryClient();
  const config = queryClient.getQueryData<SmartContractVerificationConfig>(getResourceKey('contract_verification_config'));
  const descColor = useColorModeValue('#616161', '#747474');

  const handleCheckboxChange = React.useCallback(() => {
    if (isNightly) {
      const field = getValues('compiler');
      field?.value.includes('nightly') && resetField('compiler', { defaultValue: null });
    }
    setIsNightly(prev => !prev);
  }, [ getValues, isNightly, resetField ]);

  const options = React.useMemo(() => {
    const versions = (() => {
      if (isStylus) {
        return config?.stylus_compiler_versions;
      }
      if (isVyper) {
        return config?.vyper_compiler_versions;
      }
      return config?.solidity_compiler_versions;
    })();

    return versions?.map((option) => ({ label: option, value: option })) || [];
  }, [ isStylus, isVyper, config?.solidity_compiler_versions, config?.stylus_compiler_versions, config?.vyper_compiler_versions ]);

  const loadOptions = React.useCallback(async(inputValue: string) => {
    return options
      .filter(({ label }) => !inputValue || label.toLowerCase().includes(inputValue.toLowerCase()))
      .filter(({ label }) => isNightly ? true : !label.includes('nightly'))
      .slice(0, OPTIONS_LIMIT);
  }, [ isNightly, options ]);

  return (
    <ContractVerificationFormRow>
      <>
        { !isVyper && !isStylus && (
          <Checkbox
            size="lg"
            mb={ 2 }
            onChange={ handleCheckboxChange }
            isDisabled={ formState.isSubmitting }
          >
            { t(CONTRACT_VERIFICATION_FIELD_KEYS.compiler.includeNightly) }
          </Checkbox>
        ) }
        <FormFieldFancySelect<FormFields, 'compiler'>
          name="compiler"
          placeholder={ t(CONTRACT_VERIFICATION_FIELD_KEYS.compiler.placeholder) }
          loadOptions={ loadOptions }
          defaultOptions
          placeholderIcon={ <IconSvg name="search"/> }
          isRequired
          isAsync
        />
      </>
      { isVyper || isStylus ? null : (
        <chakra.div
          mt={{ base: 0, lg: 8 }}
          color={ descColor }
          fontSize="16px"
          fontWeight={ 500 }
          lineHeight="24px"
          fontFamily="'Space Grotesk', sans-serif"
        >
          <chakra.span>{ t(CONTRACT_VERIFICATION_FIELD_KEYS.compiler.descriptionPart1) } </chakra.span>
          <Code
            color="inherit"
            fontSize="inherit"
            fontFamily="mono"
            fontWeight={ 500 }
            bg="transparent"
            px={ 0 }
          >
            pragma solidity X.X.X
          </Code>
          <chakra.span>{ t(CONTRACT_VERIFICATION_FIELD_KEYS.compiler.descriptionPart2) } </chakra.span>
          <Code
            color="inherit"
            fontSize="inherit"
            fontFamily="mono"
            fontWeight={ 500 }
            bg="transparent"
            px={ 0 }
          >
            solc --version
          </Code>
          <chakra.span> { t(CONTRACT_VERIFICATION_FIELD_KEYS.compiler.descriptionPart3) }</chakra.span>
        </chakra.div>
      ) }
    </ContractVerificationFormRow>
  );
};

export default React.memo(ContractVerificationFieldCompiler);
